import * as dto from '@epicuro-next/data/baskets-dto';
import { isNullish } from '@epicuro-next/utilities';
import { BasketRowProductPrepack } from 'graphql-api/gql-api-interfaces';

/**
 * Ensures that product is a prepack.
 */
export function toPrepack(product: dto.ReferencedProduct) {
  const hasCompositionPart =
    !!product?.productCompositionPartReferences?.length;
  if (!hasCompositionPart) {
    return undefined;
  }

  return product;
}

/**
 * Calculates prepack size from composition parts.
 */
export function getPrepackSize(product: dto.ReferencedProduct | undefined) {
  return (
    product?.productCompositionPartReferences?.reduce(
      (sum, part) => sum + part.amount,
      0,
    ) ?? 0
  );
}

/**
 * Creates map of product keys to products.
 * Product key is a combination of brand id and product id.
 */
export function getProductsMap(basketDto: dto.DtoBasket): dto.ProductsMap {
  const map: dto.ProductsMap = {};
  for (const product of basketDto.referencedProducts) {
    map[getProductKey(product.brand.brandId, product.productId)] = product;
  }

  return map;
}

/**
 * Find product by unique pair of ids.
 */
export function findProduct(
  product: { brandId: string; productId: string },
  productsMap: dto.ProductsMap,
) {
  return productsMap[getProductKey(product.brandId, product.productId)];
}

export function getProductInfo(
  product: dto.ReferencedProduct,
  productsMap: dto.ProductsMap,
): dto.IProductInfo {
  const isPrepack = toPrepack(product) !== undefined;
  const productType = isPrepack
    ? dto.ProductType.Prepack
    : dto.ProductType.Size;

  const article = getArticle(product, productType, productsMap);
  const material = getMaterialForSize(product, productsMap);
  const prepackMaterialIds = getMaterialIdsForPrepack(product, productsMap);

  return {
    prepackMaterialIds,
    materialId: material?.productId,
    articleId: article.productId,
  };
}

/**
 * Find product in hierarchy.
 */
export function findProductByType(
  product: dto.ReferencedProduct,
  productsMap: dto.ProductsMap,
  productType: dto.ProductType,
): dto.ReferencedProduct | undefined {
  if (getProductType(product.productClassId) === productType) {
    return product;
  }

  if (isNullish(product.masterProductId)) {
    return undefined;
  }

  const masterProduct = findProduct(
    {
      brandId: product.brand.brandId,
      productId: product.masterProductId,
    },
    productsMap,
  );

  if (isNullish(masterProduct)) {
    return undefined;
  }

  return findProductByType(masterProduct, productsMap, productType);
}

/**
 * Creates unique key for product.
 */
export function getProductKey(brandId: string, productId: string) {
  return `${brandId}:${productId}`;
}

/**
 * Group items by materials.
 */
export function groupItemsByMaterial(
  items: dto.Item[],
  productsMap: dto.ProductsMap,
) {
  return items
    .map((item) => {
      const sizeOrPrepack = findProduct(item, productsMap);
      const prepack = toPrepack(sizeOrPrepack);
      if (prepack)
        return {
          item,
          materials: getDistinctMaterialsForPrepack(sizeOrPrepack, productsMap),
        };

      const material = getMaterialForSize(sizeOrPrepack, productsMap);
      if (material)
        return {
          item,
          materials: [material],
        };

      throw new Error(`Expected size or prepack for ${item.productId}`);
    })
    .flatMap((pair) =>
      pair.materials.map((material) => ({
        productId: material.productId,
        brandId: material.brand.brandId,
        item: pair.item,
      })),
    )
    .reduce((result, next) => {
      const productKey = getProductKey(next.brandId, next.productId);
      return {
        ...result,
        [productKey]: [...(result[productKey] || []), next.item],
      };
    }, {} as { [materialKey: string]: dto.Item[] });
}

/**
 * Creates map of article ids to items that belongs to that article.
 * Actual or 3 level hierarchy only.
 */
export function groupItemsByArticle(
  items: dto.Item[],
  productsMap: dto.ProductsMap,
) {
  return items.reduce((result, item) => {
    const sizeOrPrepack = findProduct(item, productsMap);
    const prepack = toPrepack(sizeOrPrepack);
    const productType = prepack
      ? dto.ProductType.Prepack
      : dto.ProductType.Size;
    const article = getArticle(
      prepack ?? sizeOrPrepack,
      productType,
      productsMap,
    );

    if (!article) {
      return result;
    }

    const productKey = getProductKey(article.brand.brandId, article.productId);

    return {
      ...result,
      [productKey]: [...(result[productKey] || []), item],
    };
  }, {} as { [articleId: string]: dto.Item[] });
}

/**
 * Creates map of brand ids to items that belongs to that brand.
 */
export function groupItemsByBrand(items: dto.Item[]) {
  return items.reduce((result, item) => {
    return {
      ...result,
      [item.brandId]: [...(result[item.brandId] || []), item],
    };
  }, {} as { [brandId: string]: dto.Item[] });
}

/**
 * Article may not be defined for 2 levels hierarchies.
 */
export function getArticle(
  product: dto.ReferencedProduct,
  productType: dto.ProductType,
  productsMap: dto.ProductsMap,
): dto.ReferencedProduct {
  let article: dto.ReferencedProduct | undefined;

  switch (productType) {
    case dto.ProductType.Size:
      article = findArticle(product, productsMap, 0);
      break;
    case dto.ProductType.Material:
      article = findArticle(product, productsMap, 1);
      break;
    case dto.ProductType.Prepack:
      const [first] = product.productCompositionPartReferences;
      article = findArticle(
        first &&
          productsMap[
            getProductKey(first.product.brandId, first.product.productId)
          ],
        productsMap,
        0,
      );
      break;
    default:
      throw new Error(
        `Not supported productType ${productType} of the product ${product.productId}`,
      );
  }

  if (isNullish(article)) {
    throw new Error(
      'Cannot find the main article of this productId: ' + product.productId,
    );
  }

  return article;
}

/**
 * Searches for article up in hierarchy.
 * Assumes, that article is on third level.
 */
function findArticle(
  product: dto.ReferencedProduct,
  productsMap: dto.ProductsMap,
  level: number,
): dto.ReferencedProduct | undefined {
  if (!product?.masterProductId) {
    return level === 2 ? product : undefined;
  }

  return findArticle(
    productsMap[getProductKey(product.brand.brandId, product.masterProductId)],
    productsMap,
    level + 1,
  );
}

/**
 * Gets material for a size product.
 */
export function getMaterialForSize(
  size: dto.ReferencedProduct,
  productsMap: dto.ProductsMap,
) {
  const hasCompositionPart = !!size?.productCompositionPartReferences?.length;
  if (!size.masterProductId || hasCompositionPart) {
    return undefined;
  }

  return productsMap[getProductKey(size.brand.brandId, size.masterProductId)];
}

export function getMaterialIdsForPrepack(
  prepack: dto.ReferencedProduct,
  productsMap: dto.ProductsMap,
) {
  const hasCompositionPart = Boolean(
    prepack?.productCompositionPartReferences?.length,
  );
  if (!hasCompositionPart) {
    return undefined;
  }

  return prepack.productCompositionPartReferences
    .map(
      ({ product }) =>
        productsMap[getProductKey(product.brandId, product.productId)]
          .masterProductId,
    )
    .filter((materialId, i, arr) => arr.indexOf(materialId) === i);
}

export function getPrepackQuantities(
  prepack: dto.ReferencedProduct,
): BasketRowProductPrepack | undefined {
  const hasCompositionPart = Boolean(
    prepack?.productCompositionPartReferences?.length,
  );
  if (!hasCompositionPart) {
    return undefined;
  }

  return {
    productId: prepack.productId,
    brandId: prepack.brand.brandId,
    quantities: prepack.productCompositionPartReferences.map((ref) => ({
      productId: ref.product.productId,
      quantity: ref.amount,
      brandId: ref.product.brandId,
    })),
  };
}

/**
 * Gets list of distinct materials that are used in the prepack.
 * It can be many.
 */
function getDistinctMaterialsForPrepack(
  prepack: dto.ReferencedProduct,
  productsMap: dto.ProductsMap,
) {
  const all = prepack.productCompositionPartReferences
    .map((part) =>
      getMaterialForSize(
        productsMap[
          getProductKey(part.product.brandId, part.product.productId)
        ],
        productsMap,
      ),
    )
    .filter((x) => x) as dto.ReferencedProduct[];

  return all.filter((obj, pos, arr) => {
    return arr.map((mapObj) => mapObj.productId).indexOf(obj.productId) === pos;
  });
}

/**
 * Gets list of size totals summed by material in a given prepack.
 */
export function getSizesTotalsByMaterialForPrepack(
  prepack: dto.ReferencedProduct,
  productsMap: dto.ProductsMap,
  prepackAmount = 1,
) {
  const materials = prepack.productCompositionPartReferences.reduce(
    (aggr, part) => {
      const material = getMaterialForSize(
        productsMap[
          getProductKey(part.product.brandId, part.product.productId)
        ],
        productsMap,
      );
      return material && material?.productId
        ? {
            ...aggr,
            [material.productId]: (aggr[material.productId] || 0) + part.amount,
          }
        : aggr;
    },
    {} as { [key: string]: number },
  );
  Object.keys(materials).forEach((key) => (materials[key] *= prepackAmount));
  return materials;
}

/**
 * Maps product type from string to enum
 */
export function getProductType(productType?: dto.ProductTypeString) {
  switch (productType) {
    case dto.ProductTypeString.Article:
      return dto.ProductType.Article;
    case dto.ProductTypeString.Material:
      return dto.ProductType.Material;
    case dto.ProductTypeString.Size:
      return dto.ProductType.Size;
    case dto.ProductTypeString.Prepack:
      return dto.ProductType.Prepack;
    default:
      return undefined;
  }
}
